import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, Star, Crown, Loader2, Lock, Sparkles, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import BottomNav from '../components/BottomNav';

const getImageUrl = (photoPath) => {
  if (!photoPath) return 'https://images.pexels.com/photos/1040881/pexels-photo-1040881.jpeg?auto=compress&cs=tinysrgb&w=300';
  if (photoPath.startsWith('http')) return photoPath;
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  return `${backendUrl}${photoPath}`;
};

const WhoLikesMe = () => {
  const [likers, setLikers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isPremium, setIsPremium] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  const { token, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchLikers = async () => {
      try {
        const res = await axios.get(`${API}/likes/received`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setLikers(res.data.users || []);
        setIsPremium(res.data.is_premium || false);
        setTotalCount(res.data.total_count || 0);
      } catch (err) {
        console.error('Error fetching likes:', err);
        toast.error('Kunne ikke hente likes');
        setLikers([]);
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchLikers();
    }
  }, [token]);

  const handleLikeBack = async (userId) => {
    try {
      await axios.post(`${API}/swipe`, 
        { target_user_id: userId, action: 'like' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Det er et match! 🎉');
      // Remove from list
      setLikers(likers.filter(l => l.id !== userId));
    } catch (err) {
      toast.error('Kunne ikke like tilbage');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-20">
      {/* Header */}
      <div className="fixed top-0 left-0 right-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-lg mx-auto px-4 py-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <Heart className="w-6 h-6 text-primary fill-primary" />
              <span className="text-lg font-display font-semibold text-foreground">Hvem kan lide mig</span>
            </div>
            {totalCount > 0 && (
              <Badge className="ml-auto bg-primary">{totalCount}</Badge>
            )}
          </div>
        </div>
      </div>

      <div className="pt-20 px-4">
        <div className="max-w-lg mx-auto">
          {likers.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                <Heart className="w-10 h-10 text-muted-foreground" />
              </div>
              <h3 className="text-xl font-display font-semibold text-foreground">
                Ingen likes endnu
              </h3>
              <p className="text-muted-foreground mt-2 max-w-xs mx-auto">
                Bliv ved med at swipe, så kommer de nok!
              </p>
              <Button onClick={() => navigate('/discover')} className="mt-6 rounded-full">
                Find Matches
              </Button>
            </div>
          ) : (
            <>
              {/* Premium upsell if not premium */}
              {!isPremium && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-gradient-to-r from-primary/10 to-accent/10 border border-primary/20 rounded-2xl p-4 mb-6"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                      <Crown className="w-6 h-6 text-primary" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-foreground">Se hvem der kan lide dig</h4>
                      <p className="text-sm text-muted-foreground">Opgrader til Premium for at se alle profiler</p>
                    </div>
                    <Button onClick={() => navigate('/premium')} size="sm" className="rounded-full">
                      Opgrader
                    </Button>
                  </div>
                </motion.div>
              )}

              {/* Likers grid */}
              <div className="grid grid-cols-2 gap-4">
                {likers.map((liker, index) => (
                  <motion.div
                    key={liker.id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.05 }}
                    className="relative group"
                  >
                    <div 
                      className={`relative aspect-[3/4] rounded-2xl overflow-hidden ${
                        liker.is_blurred ? 'cursor-pointer' : ''
                      }`}
                      onClick={() => !liker.is_blurred && navigate(`/profile/${liker.id}`)}
                    >
                      <img
                        src={getImageUrl(liker.photos?.[0])}
                        alt={liker.name}
                        className={`w-full h-full object-cover ${
                          liker.is_blurred ? 'blur-xl scale-110' : ''
                        }`}
                      />
                      
                      {/* Gradient overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                      
                      {/* Lock overlay for non-premium */}
                      {liker.is_blurred && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                          <div className="text-center">
                            <Lock className="w-8 h-8 text-white mx-auto mb-2" />
                            <span className="text-white text-sm font-medium">Premium</span>
                          </div>
                        </div>
                      )}
                      
                      {/* Super like badge */}
                      {liker.like_type === 'superlike' && (
                        <div className="absolute top-2 right-2 bg-accent rounded-full p-1.5">
                          <Star className="w-4 h-4 text-white fill-white" />
                        </div>
                      )}
                      
                      {/* Profile info */}
                      <div className="absolute bottom-0 left-0 right-0 p-3 text-white">
                        <h4 className={`font-semibold ${liker.is_blurred ? 'blur-sm' : ''}`}>
                          {liker.name}{liker.age ? `, ${liker.age}` : ''}
                        </h4>
                        {!liker.is_blurred && liker.location && (
                          <p className="text-xs text-white/80">{liker.location}</p>
                        )}
                      </div>
                    </div>
                    
                    {/* Like back button (only for premium) */}
                    {!liker.is_blurred && (
                      <Button
                        onClick={() => handleLikeBack(liker.id)}
                        className="absolute -bottom-2 left-1/2 -translate-x-1/2 rounded-full bg-primary hover:bg-primary/90 shadow-lg"
                        size="sm"
                      >
                        <Heart className="w-4 h-4 mr-1" />
                        Like tilbage
                      </Button>
                    )}
                  </motion.div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      <BottomNav />
    </div>
  );
};

export default WhoLikesMe;
